import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, Building2, CheckCircle2, ChevronDown, ChevronRight, Clock,
    Eye, ExternalLink, FileCheck2, Handshake, Landmark, Mail, MapPin, Menu,
    Phone, Scale, Send, Shield, ShieldCheck, Users2, X, Zap,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

/* ============================================================
   HELPERS DE ESTILO
   ============================================================ */

const SECTION = "px-6 lg:px-10 max-w-7xl mx-auto";

/* ============================================================
   1 · HERO
   ============================================================ */
function HeroSection({ onCta }) {
    return (
        <section
            className="relative overflow-hidden bg-[color:var(--brand-paper)]"
            data-testid="landing-hero"
        >
            {/* Imagen hero como fondo con overlay */}
            <div className="absolute inset-0 z-0">
                <img
                    src="/brand/landing/hero_executive.jpg"
                    alt=""
                    aria-hidden="true"
                    className="w-full h-full object-cover object-center opacity-[0.22]"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[color:var(--brand-paper)] via-[color:var(--brand-paper)]/85 to-[color:var(--brand-paper)]/70" />
            </div>
            <div className={`${SECTION} relative z-10 pt-20 pb-20 lg:pt-28 lg:pb-28`}>
                <div className="max-w-3xl">
                    <div className="inline-flex items-center gap-2 rounded-full border border-[color:var(--brand-charcoal)]/15 bg-white/60 px-4 py-1.5 text-[11px] uppercase tracking-[0.28em] text-[color:var(--brand-charcoal)]/80 backdrop-blur-sm"
                        data-testid="hero-badge">
                        <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--brand-dark-gold)]"></span>
                        Casa de bolsa autorizada por la CNBV
                    </div>
                    <h1 className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl leading-[1.03] tracking-tight text-[color:var(--brand-charcoal)]">
                        Servicios Fiduciarios con{" "}
                        <span className="text-[color:var(--brand-dark-gold)]">
                            estándar institucional
                        </span>
                        .
                    </h1>
                    <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-[color:var(--brand-charcoal)]/75">
                        Administramos fideicomisos de garantía, administración y
                        emisión bajo marco normativo propio, con documentos
                        verificables en línea y reporte continuo a partes y
                        autoridades.
                    </p>
                    <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-charcoal)] text-[color:var(--brand-paper)] px-4 py-1.5 text-[11px] font-semibold tracking-wide"
                        data-testid="hero-new-chip">
                        <Zap className="h-3 w-3 text-[color:var(--brand-gold)]" />
                        Nuevo · Verificable por autoridades y lenders en 1 click
                    </div>
                    <div className="mt-9 flex flex-col sm:flex-row gap-3">
                        <Button
                            size="lg"
                            onClick={onCta}
                            className="h-12 px-7 text-sm font-semibold"
                            data-testid="hero-cta-primary"
                        >
                            Agenda 20 minutos
                            <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                        <button
                            onClick={() => document.getElementById("promesas")?.scrollIntoView({ behavior: "smooth" })}
                            className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-md border border-[color:var(--brand-charcoal)]/20 text-sm font-medium text-[color:var(--brand-charcoal)] hover:bg-white/60 transition"
                            data-testid="hero-cta-secondary"
                        >
                            Ver cómo funciona
                            <ChevronDown className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.22em] text-[color:var(--brand-charcoal)]/55">
                        <span className="flex items-center gap-1.5">
                            <ShieldCheck className="h-3.5 w-3.5" /> Autorizada CNBV
                        </span>
                        <span className="hidden sm:inline">·</span>
                        <span>SPEI</span>
                        <span className="hidden sm:inline">·</span>
                        <span>SAT</span>
                        <span className="hidden sm:inline">·</span>
                        <span>Protocolo NOM-151</span>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ============================================================
   2 · PAINS
   ============================================================ */
function PainsSection() {
    const pains = [
        {
            titulo: "Esperas 30 días por un estado de cuenta",
            desc: "Y cuando llega es un PDF escaneado con sellos borrosos. Imposible reconciliar.",
        },
        {
            titulo: "No sabes si la cuenta tiene lo que debería",
            desc: "El fiduciario no te contesta hasta el día 10 del mes. Mientras, tu auditor sigue preguntando.",
        },
        {
            titulo: "Tu acreedor pide dossier y tomas 2 semanas",
            desc: "Compilar carpetas, enviar correos, pedir sellos. Y aun así el lender duda.",
        },
    ];
    return (
        <section className="py-20 lg:py-24 bg-white" data-testid="landing-pains">
            <div className={SECTION}>
                <div className="max-w-2xl mb-12">
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-coral)] font-semibold mb-3">
                        El fiduciario que conoces hoy
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl text-[color:var(--brand-charcoal)]">
                        ¿Te suena esto?
                    </h2>
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                    {pains.map((p, i) => (
                        <div
                            key={i}
                            className="relative p-7 rounded-lg border border-[color:var(--brand-charcoal)]/10 bg-[color:var(--brand-paper)]/40"
                            data-testid={`pain-card-${i}`}
                        >
                            <div className="font-display text-5xl leading-none text-[color:var(--brand-coral)]/25 absolute top-4 right-5">
                                0{i + 1}
                            </div>
                            <h3 className="font-display text-xl mb-3 text-[color:var(--brand-charcoal)] pr-10">
                                {p.titulo}
                            </h3>
                            <p className="text-sm leading-relaxed text-[color:var(--brand-charcoal)]/70">
                                {p.desc}
                            </p>
                        </div>
                    ))}
                </div>
                <div className="mt-14 border-l-4 border-[color:var(--brand-dark-gold)] pl-6 max-w-2xl">
                    <p className="font-display text-xl md:text-2xl text-[color:var(--brand-charcoal)] leading-snug">
                        Esto no debería ser normal.{" "}
                        <span className="text-[color:var(--brand-dark-gold)]">
                            Y con Trafalgar no lo es.
                        </span>
                    </p>
                </div>
            </div>
        </section>
    );
}

/* ============================================================
   3 · PROMESAS
   ============================================================ */
function PromisesSection() {
    const promesas = [
        {
            icon: Scale,
            titulo: "Un comité fiduciario resolviendo tu caso en 10 días",
            desc: "Pipeline documentado de siete pasos con reloj a la vista. Dictamen vigente 90 días para que firmes cuando tu operación esté lista.",
            metric: "Comité semanal · SLA cumplido en el 100% de las solicitudes estándar.",
        },
        {
            icon: FileCheck2,
            titulo: "Precio claro antes de firmar",
            desc: "Cotizador con tarifario público por producto y fórmula abierta. Cada propuesta genera un folio verificable que tu contraparte puede confirmar sin pedirte nada.",
            metric: "Seis productos tarificados · propuesta firmable en 48 horas.",
        },
        {
            icon: ShieldCheck,
            titulo: "Reportes auditables sellados con hora oficial",
            desc: "Estados de cuenta mensuales con sello temporal NOM-151. Cualquier auditor o autoridad valida la fecha original sin fricción.",
            metric: "Sello temporal automático en estados de cuenta, dictámenes y actas.",
        },
    ];
    return (
        <section id="promesas" className="py-20 lg:py-24 bg-[color:var(--brand-paper)]" data-testid="landing-promesas">
            <div className={SECTION}>
                <div className="max-w-2xl mb-14">
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-3">
                        Nuestra promesa
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl text-[color:var(--brand-charcoal)]">
                        Tres cosas que cambian cómo opera tu fideicomiso
                    </h2>
                </div>
                <div className="grid md:grid-cols-3 gap-8">
                    {promesas.map((p, i) => (
                        <div
                            key={i}
                            className="group p-7 rounded-lg bg-white border border-[color:var(--brand-charcoal)]/10 hover:border-[color:var(--brand-dark-gold)]/50 hover:shadow-lg transition-all"
                            data-testid={`promise-card-${i}`}
                        >
                            <div className="h-11 w-11 rounded-md bg-[color:var(--brand-charcoal)] text-[color:var(--brand-gold)] flex items-center justify-center mb-5 group-hover:bg-[color:var(--brand-dark-gold)] group-hover:text-white transition-colors">
                                <p.icon className="h-5 w-5" />
                            </div>
                            <h3 className="font-display text-xl md:text-2xl text-[color:var(--brand-charcoal)] mb-4 leading-snug">
                                {p.titulo}
                            </h3>
                            <p className="text-sm leading-relaxed text-[color:var(--brand-charcoal)]/70 mb-5">
                                {p.desc}
                            </p>
                            <div className="pt-4 border-t border-dashed border-[color:var(--brand-charcoal)]/15">
                                <div className="text-[11px] uppercase tracking-widest text-[color:var(--brand-dark-gold)] font-semibold mb-1">
                                    Dato verificable
                                </div>
                                <p className="text-xs text-[color:var(--brand-charcoal)]/70 leading-relaxed">
                                    {p.metric}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ============================================================
   4 · AUDIENCIAS
   ============================================================ */
function AudienceSection({ onCta }) {
    const items = [
        {
            id: "fideicomitentes",
            icon: Building2,
            etiqueta: "Fideicomitentes",
            pregunta: "¿Vas a constituir un fideicomiso?",
            copy: "Te decimos en 10 días hábiles si lo aceptamos y a qué precio. Cotización firmable en 48 horas y dictamen vigente 90 días para que firmes sin presión.",
        },
        {
            id: "fideicomisarios",
            icon: Users2,
            etiqueta: "Fideicomisarios",
            pregunta: "¿Eres beneficiario y no te enteras de nada?",
            copy: "Recibes tu estado de cuenta mensual verificable en línea con sello temporal. Cualquiera confirma su autenticidad sin pedirte nada.",
        },
        {
            id: "comites",
            icon: Scale,
            etiqueta: "Comités técnicos",
            pregunta: "¿Firmas en un comité y quieres orden?",
            copy: "Actas, dictámenes y votaciones registrados con sello temporal oficial. Lo que decidieron queda firme, lo que no se votó no se ejecuta.",
        },
        {
            id: "lenders",
            icon: Handshake,
            etiqueta: "Lenders",
            pregunta: "¿Prestaste y necesitas evidencia?",
            copy: "Portafolio verificable 24/7 con borrowing base diaria, covenants medidos y estados de cuenta auditables con fecha oficial inmutable.",
        },
        {
            id: "autoridades",
            icon: Landmark,
            etiqueta: "Autoridades",
            pregunta: "¿Autoridad pide dossier y lo quieres ya?",
            copy: "Ventanilla única con respuesta trazable, acuse firmado y archivo de concentración documental conforme a retención legal.",
        },
    ];

    return (
        <section id="audiencia" className="py-20 lg:py-24 bg-white" data-testid="landing-audiencia">
            <div className={SECTION}>
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                    <div className="lg:col-span-4">
                        <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-3">
                            Para quién es esto
                        </div>
                        <h2 className="font-display text-3xl md:text-4xl text-[color:var(--brand-charcoal)] mb-6 leading-tight">
                            Cinco perfiles, un mismo problema.
                        </h2>
                        <p className="text-sm text-[color:var(--brand-charcoal)]/70 leading-relaxed mb-6">
                            Si estás en cualquiera de estos escenarios, es muy probable
                            que la opacidad del fiduciario tradicional te esté costando
                            tiempo, dinero o conciliación con tus contrapartes.
                        </p>
                        <div className="relative aspect-[4/5] rounded-lg overflow-hidden border border-[color:var(--brand-charcoal)]/10">
                            <img
                                src="/brand/landing/hero_team.jpg"
                                alt="Equipo Trafalgar"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--brand-charcoal)]/80 via-transparent to-transparent"></div>
                            <div className="absolute bottom-5 left-5 right-5 text-[color:var(--brand-paper)]">
                                <div className="text-[10px] uppercase tracking-widest text-[color:var(--brand-gold)] mb-1">
                                    Trafalgar · TRF Casa de Bolsa
                                </div>
                                <div className="font-display text-xl leading-tight">
                                    Un equipo fiduciario que opera con reglas visibles.
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-8 space-y-4">
                        {items.map((a) => (
                            <article
                                key={a.id}
                                id={a.id}
                                className="group scroll-mt-24 rounded-lg border border-[color:var(--brand-charcoal)]/10 bg-[color:var(--brand-paper)]/30 hover:bg-[color:var(--brand-paper)]/80 hover:border-[color:var(--brand-dark-gold)]/40 p-6 md:p-7 transition-all"
                                data-testid={`audience-${a.id}`}
                            >
                                <div className="flex items-start gap-5">
                                    <div className="h-11 w-11 rounded-md border border-[color:var(--brand-charcoal)]/15 bg-white flex items-center justify-center shrink-0">
                                        <a.icon className="h-5 w-5 text-[color:var(--brand-dark-gold)]" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--brand-charcoal)]/60 font-semibold mb-1">
                                            {a.etiqueta}
                                        </div>
                                        <h3 className="font-display text-xl md:text-2xl text-[color:var(--brand-charcoal)] leading-snug mb-2">
                                            {a.pregunta}
                                        </h3>
                                        <p className="text-sm text-[color:var(--brand-charcoal)]/75 leading-relaxed mb-3">
                                            {a.copy}
                                        </p>
                                        <button
                                            onClick={() => onCta(a.id)}
                                            className="inline-flex items-center gap-1 text-xs font-semibold text-[color:var(--brand-dark-gold)] hover:gap-2 transition-all"
                                            data-testid={`audience-${a.id}-cta`}
                                        >
                                            Hablar con un asesor
                                            <ChevronRight className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ============================================================
   5 · STATS BAR
   ============================================================ */
function StatsBar() {
    const [stats, setStats] = useState([
        { num: "+300", lbl: "dictámenes vigentes del comité fiduciario" },
        { num: "+250", lbl: "estados de cuenta emitidos último mes" },
        { num: "+500", lbl: "sellos temporales NOM-151 activos" },
        { num: "+25,000", lbl: "eventos en bitácora criptográfica" },
    ]);
    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const r = await fetch("/api/public/landing-stats");
                if (!r.ok) return;
                const d = await r.json();
                if (cancel) return;
                const fmt = (n) => {
                    if (!n || n < 0) return "—";
                    if (n >= 1000) return `+${Math.round(n / 100) * 100 >= 1000
                        ? (Math.round(n / 1000)).toLocaleString() + "k" : n.toLocaleString()}`;
                    return `+${n}`;
                };
                setStats([
                    { num: fmt(d.dictamenes_vigentes), lbl: "dictámenes vigentes del comité fiduciario" },
                    { num: fmt(d.estados_cuenta_ultimo_mes), lbl: "estados de cuenta emitidos último mes" },
                    { num: fmt(d.sellos_nom151_activos), lbl: "sellos temporales NOM-151 activos" },
                    { num: fmt(d.bitacora_total_eventos), lbl: "eventos en bitácora criptográfica" },
                ]);
            } catch { /* ignore */ }
        })();
        return () => { cancel = true; };
    }, []);
    return (
        <section
            className="py-16 lg:py-20 bg-[color:var(--brand-charcoal)] text-[color:var(--brand-paper)]"
            data-testid="landing-stats"
        >
            <div className={SECTION}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-6">
                    {stats.map((s, i) => (
                        <div key={i} className="text-center md:text-left" data-testid={`stat-${i}`}>
                            <div className="font-display text-4xl md:text-5xl text-[color:var(--brand-gold)] leading-none mb-2">
                                {s.num}
                            </div>
                            <div className="text-xs uppercase tracking-widest text-[rgba(251,249,245,0.7)] leading-snug">
                                {s.lbl}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-10 pt-6 border-t border-[rgba(251,249,245,0.15)] text-center md:text-left">
                    <p className="text-[11px] text-[rgba(251,249,245,0.5)]">
                        Métricas en vivo desde el endpoint público de la plataforma.
                        <a
                            href="/api/public/landing-stats"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 text-[color:var(--brand-gold)] hover:underline inline-flex items-center gap-1"
                            data-testid="stats-verify-link"
                        >
                            Ver endpoint <ExternalLink className="h-3 w-3" />
                        </a>
                    </p>
                </div>
            </div>
        </section>
    );
}

/* ============================================================
   6 · COMPARATIVA
   ============================================================ */
function ComparisonSection() {
    const rows = [
        {
            banco: "Estado de cuenta mensual en PDF",
            trafalgar: "Estado de cuenta vivo con link verificable",
            beneficio: "Tu contraparte no te vuelve a llamar",
        },
        {
            banco: "Comité decidiendo tu caso en meses",
            trafalgar: "Comité fiduciario resolviendo en 10 días hábiles",
            beneficio: "Firmas cuando quieras, dictamen vigente 90 días",
        },
        {
            banco: "Documentos con sello interno del banco",
            trafalgar: "Documentos con sello temporal NOM-151 verificable",
            beneficio: "Evidencia aceptable ante cualquier autoridad",
        },
        {
            banco: "Reporte manual al Consejo",
            trafalgar: "Reporte trimestral automático",
            beneficio: "Cumples con la autoridad sin esfuerzo",
        },
        {
            banco: "Honorarios opacos, cotización lenta",
            trafalgar: "Cotizador con tarifario público verificable por folio",
            beneficio: "Sabes qué pagas antes de firmar",
        },
        {
            banco: "Opex escalado por operación",
            trafalgar: "Honorarios indexados a INPC",
            beneficio: "Previsibilidad presupuestal real",
        },
    ];
    return (
        <section className="py-20 lg:py-24 bg-[color:var(--brand-paper)]" data-testid="landing-comparativa">
            <div className={SECTION}>
                <div className="max-w-2xl mb-12">
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-3">
                        Trafalgar vs. banco fiduciario tradicional
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl text-[color:var(--brand-charcoal)]">
                        Lo que cambia, en una tabla.
                    </h2>
                </div>
                <div className="-mx-6 lg:mx-0 overflow-x-auto">
                    <div className="min-w-[640px] lg:min-w-0 mx-6 lg:mx-0 rounded-lg border border-[color:var(--brand-charcoal)]/10 bg-white">
                        <table className="w-full text-sm" data-testid="comparison-table">
                        <thead>
                            <tr className="bg-[color:var(--brand-charcoal)] text-[color:var(--brand-paper)]">
                                <th className="text-left px-5 py-4 font-semibold text-xs uppercase tracking-widest">
                                    Banco tradicional
                                </th>
                                <th className="text-left px-5 py-4 font-semibold text-xs uppercase tracking-widest text-[color:var(--brand-gold)]">
                                    Trafalgar
                                </th>
                                <th className="text-left px-5 py-4 font-semibold text-xs uppercase tracking-widest">
                                    Lo que ganas
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr
                                    key={i}
                                    className={`${i % 2 === 0 ? "bg-white" : "bg-[color:var(--brand-paper)]/40"} border-t border-[color:var(--brand-charcoal)]/5`}
                                    data-testid={`compare-row-${i}`}
                                >
                                    <td className="px-5 py-4 text-[color:var(--brand-charcoal)]/60 line-through decoration-[color:var(--brand-coral)]/40">
                                        {r.banco}
                                    </td>
                                    <td className="px-5 py-4 font-medium text-[color:var(--brand-charcoal)]">
                                        <div className="flex items-start gap-2">
                                            <CheckCircle2 className="h-4 w-4 text-[color:var(--brand-dark-gold)] shrink-0 mt-0.5" />
                                            <span>{r.trafalgar}</span>
                                        </div>
                                    </td>
                                    <td className="px-5 py-4 text-sm text-[color:var(--brand-charcoal)]/80 italic">
                                        {r.beneficio}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ============================================================
   7 · CASO DE USO
   ============================================================ */
function CaseStudySection() {
    const pasos = [
        {
            n: "01",
            titulo: "Firmó su solicitud en una tarde",
            desc: "Expediente generado desde el formato de solicitud oficial con los adjuntos pre-llenados. El pipeline arrancó automáticamente con reloj a la vista.",
        },
        {
            n: "02",
            titulo: "Dictamen del comité en 10 días hábiles",
            desc: "Las seis dimensiones evaluadas con calificación visible. Dictamen firmado por mayoría con vigencia 90 días para firmar sin presión.",
        },
        {
            n: "03",
            titulo: "Cotización firmada por el cliente en 48 horas",
            desc: "Propuesta con folio público. Sus tres acreedores confirmaron la autenticidad desde la landing sin pedir confirmación por correo.",
        },
        {
            n: "04",
            titulo: "Firma institucional con sello temporal",
            desc: "Convenio sellado con hora oficial inmutable. La línea de $200M quedó documentada con evidencia aceptable ante cualquier autoridad.",
        },
    ];
    return (
        <section className="py-20 lg:py-28 bg-white" data-testid="landing-caso">
            <div className={SECTION}>
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    <div className="order-2 lg:order-1 relative aspect-[4/3] rounded-lg overflow-hidden border border-[color:var(--brand-charcoal)]/10">
                        <img
                            src="/brand/landing/hero_handshake.jpg"
                            alt="Firma de operación"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-tr from-[color:var(--brand-charcoal)]/40 to-transparent"></div>
                        <div className="absolute bottom-6 left-6 right-6">
                            <div className="inline-flex items-center gap-2 bg-[color:var(--brand-paper)]/95 backdrop-blur-sm rounded-full px-4 py-1.5 text-[11px] uppercase tracking-widest text-[color:var(--brand-charcoal)] font-semibold">
                                <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--brand-dark-gold)]"></span>
                                Caso ilustrativo
                            </div>
                        </div>
                    </div>
                    <div className="order-1 lg:order-2">
                        <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-3">
                            Cómo opera un cliente en Trafalgar
                        </div>
                        <h2 className="font-display text-3xl md:text-4xl text-[color:var(--brand-charcoal)] leading-tight mb-8">
                            Una SOFIPO cerró su línea de $200M con tres lenders.
                        </h2>
                        <ol className="space-y-6">
                            {pasos.map((p) => (
                                <li key={p.n} className="flex gap-5" data-testid={`case-step-${p.n}`}>
                                    <div className="font-display text-3xl text-[color:var(--brand-dark-gold)] leading-none shrink-0 w-12">
                                        {p.n}
                                    </div>
                                    <div>
                                        <h3 className="font-display text-lg text-[color:var(--brand-charcoal)] mb-1">
                                            {p.titulo}
                                        </h3>
                                        <p className="text-sm text-[color:var(--brand-charcoal)]/70 leading-relaxed">
                                            {p.desc}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                        <p className="mt-8 text-[11px] italic text-[color:var(--brand-charcoal)]/50">
                            Caso ilustrativo basado en escenarios reales del sector.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ============================================================
   8 · TRUST BADGES
   ============================================================ */
function TrustBadges() {
    const sellos = [
        { icon: Landmark, label: "Autorizada CNBV" },
        { icon: FileCheck2, label: "Protocolo NOM-151" },
        { icon: Shield, label: "Bitácora criptográfica inmutable" },
        { icon: Scale, label: "Reporte trimestral al Consejo" },
        { icon: CheckCircle2, label: "Gobierno fiduciario documentado" },
        { icon: ShieldCheck, label: "Aislamiento multi-tenant por fideicomiso" },
    ];
    return (
        <section className="py-16 bg-[color:var(--brand-paper)] border-y border-[color:var(--brand-charcoal)]/10"
            data-testid="landing-sellos">
            <div className={SECTION}>
                <div className="text-center max-w-2xl mx-auto mb-10">
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Sellos de confianza
                    </div>
                    <p className="text-sm text-[color:var(--brand-charcoal)]/70">
                        Lo que sostiene cada operación de Trafalgar por debajo del tablero.
                    </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                    {sellos.map((s, i) => (
                        <div
                            key={i}
                            className="flex flex-col items-center text-center p-4 rounded-md hover:bg-white/60 transition"
                            data-testid={`trust-${i}`}
                        >
                            <s.icon className="h-7 w-7 text-[color:var(--brand-dark-gold)] mb-3" />
                            <div className="text-xs font-medium text-[color:var(--brand-charcoal)] leading-tight">
                                {s.label}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ============================================================
   9 · CONTACT FORM
   ============================================================ */
const TIPOS_FID = [
    { value: "garantia", label: "Garantía" },
    { value: "administracion", label: "Administración" },
    { value: "escrow", label: "Escrow" },
];

const PATRIMONIO_RANGOS = [
    { value: "menos_10m", label: "Menos de $10 MDP" },
    { value: "entre_10m_100m", label: "$10 – $100 MDP" },
    { value: "entre_100m_500m", label: "$100 – $500 MDP" },
    { value: "mas_500m", label: "Más de $500 MDP" },
    { value: "no_especificado", label: "Aún no definido" },
];

const ROLES_LEAD = [
    "Fideicomitente",
    "Fideicomisario",
    "Comité técnico",
    "Lender",
    "Autoridad",
    "Otro",
];

// Mapeo de audiencia (anchor de card) → rol del dropdown
const AUDIENCIA_TO_ROL = {
    fideicomitentes: "Fideicomitente",
    fideicomisarios: "Fideicomisario",
    comites: "Comité técnico",
    lenders: "Lender",
    autoridades: "Autoridad",
};

function ContactForm({ initialRol = "" }) {
    // Fallback robusto: si parent aún no pasó preRol, leemos el param directo
    const _computeInitialRol = () => {
        if (initialRol) return initialRol;
        if (typeof window !== "undefined") {
            const aud = new URLSearchParams(window.location.search).get("audiencia");
            if (aud && AUDIENCIA_TO_ROL[aud]) return AUDIENCIA_TO_ROL[aud];
        }
        return "";
    };

    const [nombre, setNombre] = useState("");
    const [empresa, setEmpresa] = useState("");
    const [rol, setRol] = useState(_computeInitialRol);
    const [email, setEmail] = useState("");
    const [telefono, setTelefono] = useState("");
    const [patrimonio, setPatrimonio] = useState("");
    const [tipos, setTipos] = useState([]);
    const [mensaje, setMensaje] = useState("");
    const [sending, setSending] = useState(false);
    const [success, setSuccess] = useState(false);

    // Si cambia el initialRol (nueva audiencia clickeada), re-aplicarlo
    useEffect(() => {
        if (initialRol) setRol(initialRol);
    }, [initialRol]);

    const toggleTipo = (v) =>
        setTipos((p) => p.includes(v) ? p.filter((x) => x !== v) : [...p, v]);

    const reset = () => {
        setNombre(""); setEmpresa(""); setRol(initialRol || ""); setEmail("");
        setTelefono(""); setPatrimonio(""); setTipos([]); setMensaje("");
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!nombre || !empresa || !rol || !email || !mensaje) {
            toast.error("Completa los campos obligatorios"); return;
        }
        if (mensaje.length < 10) {
            toast.error("Cuéntanos un poco más (mínimo 10 caracteres)"); return;
        }
        setSending(true);
        try {
            // patrimonio_estimado ahora es un campo estructurado del schema
            const patrimonio_estimado = patrimonio || "no_especificado";
            await api.post("/leads", {
                nombre, empresa, rol, email,
                telefono: telefono || "No proporcionado",
                tipos_fideicomiso: tipos,
                mensaje,
                patrimonio_estimado,
            });
            setSuccess(true);
            reset();
            setTimeout(() => setSuccess(false), 12000);
        } catch (err) {
            if (err?.response?.status === 429) {
                toast.error("Demasiadas solicitudes. Intenta en 1 minuto.");
            } else {
                toast.error("No pudimos enviar tu solicitud", {
                    description: err?.response?.data?.detail?.slice?.(0, 200) || "Verifica los datos",
                });
            }
        } finally { setSending(false); }
    };

    if (success) {
        return (
            <div
                className="rounded-lg border-2 border-[color:var(--brand-exito)]/40 bg-[color:var(--brand-exito)]/5 p-10 text-center"
                data-testid="form-success"
            >
                <CheckCircle2 className="h-12 w-12 text-[color:var(--brand-exito)] mx-auto mb-4" />
                <h3 className="font-display text-2xl mb-2">Recibimos tu solicitud</h3>
                <p className="text-sm text-[color:var(--brand-charcoal)]/70 max-w-md mx-auto">
                    Un asesor del equipo Trafalgar te contactará dentro de 2 días hábiles
                    para agendar la llamada.
                </p>
                <Button variant="outline" className="mt-6" onClick={() => setSuccess(false)}>
                    Enviar otra solicitud
                </Button>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="space-y-5" data-testid="lead-form">
            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <label className="text-[11px] uppercase tracking-widest text-[color:var(--brand-charcoal)]/60">
                        Nombre *
                    </label>
                    <Input value={nombre} onChange={(e) => setNombre(e.target.value)}
                        className="mt-1" data-testid="form-nombre" required />
                </div>
                <div>
                    <label className="text-[11px] uppercase tracking-widest text-[color:var(--brand-charcoal)]/60">
                        Empresa *
                    </label>
                    <Input value={empresa} onChange={(e) => setEmpresa(e.target.value)}
                        className="mt-1" data-testid="form-empresa" required />
                </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <label className="text-[11px] uppercase tracking-widest text-[color:var(--brand-charcoal)]/60">
                        Email corporativo *
                    </label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                        className="mt-1" data-testid="form-email" required />
                </div>
                <div>
                    <label className="text-[11px] uppercase tracking-widest text-[color:var(--brand-charcoal)]/60">
                        Teléfono
                    </label>
                    <Input value={telefono} onChange={(e) => setTelefono(e.target.value)}
                        placeholder="Opcional"
                        className="mt-1" data-testid="form-telefono" />
                </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <label className="text-[11px] uppercase tracking-widest text-[color:var(--brand-charcoal)]/60">
                        Rol *
                    </label>
                    <Select value={rol} onValueChange={setRol}>
                        <SelectTrigger className="mt-1" data-testid="form-rol">
                            <SelectValue placeholder="Selecciona..." />
                        </SelectTrigger>
                        <SelectContent>
                            {ROLES_LEAD.map((r) => (
                                <SelectItem key={r} value={r}>{r}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <label className="text-[11px] uppercase tracking-widest text-[color:var(--brand-charcoal)]/60">
                        Patrimonio del fideicomiso
                    </label>
                    <Select value={patrimonio} onValueChange={setPatrimonio}>
                        <SelectTrigger className="mt-1" data-testid="form-patrimonio">
                            <SelectValue placeholder="Selecciona rango..." />
                        </SelectTrigger>
                        <SelectContent>
                            {PATRIMONIO_RANGOS.map((r) => (
                                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div>
                <label className="text-[11px] uppercase tracking-widest text-[color:var(--brand-charcoal)]/60">
                    Tipo de fideicomiso de interés
                </label>
                <div className="flex gap-2 mt-2 flex-wrap" data-testid="form-tipo">
                    {TIPOS_FID.map((t) => (
                        <button key={t.value} type="button" onClick={() => toggleTipo(t.value)}
                            className={`px-4 py-2 rounded-sm border text-xs uppercase tracking-widest transition ${
                                tipos.includes(t.value)
                                    ? "bg-[color:var(--brand-charcoal)] text-[color:var(--brand-paper)] border-[color:var(--brand-charcoal)]"
                                    : "border-[color:var(--brand-charcoal)]/20 text-[color:var(--brand-charcoal)]/70 hover:border-[color:var(--brand-dark-gold)]"
                            }`}>
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>
            <div>
                <label className="text-[11px] uppercase tracking-widest text-[color:var(--brand-charcoal)]/60">
                    Cuéntanos qué necesitas *
                </label>
                <textarea value={mensaje} onChange={(e) => setMensaje(e.target.value)}
                    className="mt-1 w-full min-h-[110px] rounded-md border border-[color:var(--brand-charcoal)]/20 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-dark-gold)]/40"
                    placeholder="Tipo de operación, timing esperado, acreedores involucrados..."
                    data-testid="form-mensaje" required minLength={10} maxLength={3000} />
                <div className="text-[10px] text-[color:var(--brand-charcoal)]/50 text-right mt-1">
                    {mensaje.length}/3000
                </div>
            </div>
            <Button type="submit" size="lg" className="w-full h-12" disabled={sending}
                data-testid="form-submit">
                {sending ? "Enviando…" : (
                    <><Send className="h-4 w-4 mr-2" /> Agendar llamada de 20 minutos</>
                )}
            </Button>
            <p className="text-[11px] text-center text-[color:var(--brand-charcoal)]/50">
                Un asesor te contacta en menos de 2 días hábiles. Sin spam ni listas de distribución.
            </p>
        </form>
    );
}

/* ============================================================
   10 · FAQ
   ============================================================ */
function FAQSection() {
    const faqs = [
        {
            q: "¿Son casa de bolsa autorizada?",
            a: "Sí. Trafalgar Servicios Fiduciarios es la Dirección Fiduciaria de TRF Casa de Bolsa, S.A. de C.V., autorizada por la CNBV conforme al artículo 171 fracción XIV de la Ley del Mercado de Valores.",
        },
        {
            q: "¿Qué significa que mis documentos tengan sello NOM-151?",
            a: "El sello NOM-151 es una constancia de tiempo oficial que fija de forma inmutable la fecha y hora en que se emitió el documento. Está regulado por la Secretaría de Economía y es aceptado por autoridades, auditores y cortes como evidencia de que ese documento existía exactamente así en esa fecha. Cada estado de cuenta, dictamen, convenio y acta que emitimos lleva este sello automáticamente.",
        },
        {
            q: "¿Mi contraparte puede verificar mis documentos sin pedirme nada?",
            a: "Sí. Toda cotización, estado de cuenta, sello temporal y portafolio que emitimos tiene un folio público. Tu contraparte abre nuestro verificador en línea, pega el folio y confirma la autenticidad en segundos, sin registrarse, sin credenciales y sin pedirte una copia por correo.",
        },
        {
            q: "¿Cuánto cuesta?",
            a: "Tenemos tarifario público por tipo de fideicomiso. Después de la primera llamada te damos cotización formal en 48 horas, con todos los conceptos a la vista. Cada propuesta tiene folio verificable y una vigencia de 30 días.",
        },
        {
            q: "¿Qué pasa si quiero cambiar de fiduciario y venir con ustedes?",
            a: "Hemos migrado operaciones vivas. Diseñamos la sustitución fiduciaria con tu equipo legal, acordamos ventana con tus contrapartes y hacemos el corte sin interrupción de cobranza ni cascada.",
        },
        {
            q: "¿Qué tipos de fideicomiso aceptan?",
            a: "Garantía y fuente de pago, administración y fuente de pago, escrow, inversión y administración de valores, planes de acciones y emisor de valores fiduciarios. Si tu negocio no cabe exactamente, lo evaluamos en Comité Fiduciario.",
        },
        {
            q: "¿Puedo integrar con mis bancos?",
            a: "Sí. Operamos con cuentas segregadas por fideicomiso y nos integramos vía archivos o APIs con la mayoría de bancos del sistema mexicano para automatizar la conciliación diaria.",
        },
        {
            q: "¿Es confidencial mi información?",
            a: "Cada fideicomiso vive en un espacio aislado dentro de la plataforma. Nadie fuera de las partes autorizadas ve tu información. Todo movimiento queda en una bitácora inmutable que ni el administrador puede alterar sin dejar rastro.",
        },
    ];
    const [open, setOpen] = useState(null);
    return (
        <section className="py-20 lg:py-24 bg-white" data-testid="landing-faq">
            <div className={`${SECTION} max-w-4xl`}>
                <div className="text-center mb-14">
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-3">
                        Preguntas frecuentes
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl text-[color:var(--brand-charcoal)]">
                        Lo que suelen preguntarnos
                    </h2>
                </div>
                <div className="space-y-3">
                    {faqs.map((f, i) => (
                        <div
                            key={i}
                            className="rounded-md border border-[color:var(--brand-charcoal)]/10 bg-[color:var(--brand-paper)]/40 overflow-hidden"
                            data-testid={`faq-item-${i}`}
                        >
                            <button
                                onClick={() => setOpen(open === i ? null : i)}
                                className="w-full flex items-start justify-between gap-5 px-6 py-5 text-left hover:bg-[color:var(--brand-paper)]/70 transition"
                                data-testid={`faq-toggle-${i}`}
                                aria-expanded={open === i}
                            >
                                <span className="font-display text-base md:text-lg text-[color:var(--brand-charcoal)] leading-snug">
                                    {f.q}
                                </span>
                                <ChevronDown
                                    className={`h-5 w-5 text-[color:var(--brand-dark-gold)] shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`}
                                />
                            </button>
                            {open === i && (
                                <div className="px-6 pb-5 text-sm text-[color:var(--brand-charcoal)]/75 leading-relaxed">
                                    {f.a}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ============================================================
   11 · FOOTER PÚBLICO
   ============================================================ */
/* ============================================================
   11 · VERIFICADORES PÚBLICOS (4 WIDGETS)
   ============================================================ */
function VerifWidget({ titulo, descripcion, placeholder, endpoint, parseResult, testid }) {
    const [val, setVal] = useState("");
    const [res, setRes] = useState(null);
    const [loading, setLoading] = useState(false);
    const run = async () => {
        const v = val.trim();
        if (!v) return;
        setLoading(true); setRes(null);
        try {
            const r = await fetch(endpoint(encodeURIComponent(v)));
            const data = await r.json();
            setRes(parseResult(data));
        } catch { setRes({ existe: false, detalle: "No disponible en este momento." }); }
        finally { setLoading(false); }
    };
    return (
        <div className="rounded-lg border border-[color:var(--brand-charcoal)]/10 bg-white p-5 flex flex-col gap-3"
             data-testid={`verif-widget-${testid}`}>
            <div>
                <h4 className="font-display text-base md:text-lg text-[color:var(--brand-charcoal)] leading-snug">
                    {titulo}
                </h4>
                <p className="text-xs text-[color:var(--brand-charcoal)]/65 mt-1 leading-relaxed">
                    {descripcion}
                </p>
            </div>
            <div className="flex gap-2 mt-1">
                <input
                    data-testid={`verif-input-${testid}`}
                    type="text" value={val}
                    onChange={(e) => setVal(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") run(); }}
                    placeholder={placeholder}
                    className="flex-1 px-3 py-2 rounded-md border border-[color:var(--brand-charcoal)]/15 bg-[color:var(--brand-paper)]/40 text-sm font-mono focus:outline-none focus:border-[color:var(--brand-dark-gold)]"
                />
                <button
                    data-testid={`verif-btn-${testid}`}
                    onClick={run}
                    disabled={loading || !val.trim()}
                    className="px-4 py-2 bg-[color:var(--brand-charcoal)] text-white rounded-md text-xs font-semibold hover:bg-[color:var(--brand-charcoal)]/90 disabled:opacity-50 whitespace-nowrap">
                    {loading ? "…" : "Verificar"}
                </button>
            </div>
            {res && (
                <div className="rounded-md border text-xs p-3"
                     data-testid={`verif-result-${testid}`}
                     style={{
                         background: res.existe ? "#ECFDF5" : "#FEF2F2",
                         borderColor: res.existe ? "#A7F3D0" : "#FECACA",
                         color: res.existe ? "#065F46" : "#991B1B",
                     }}>
                    <div className="font-semibold">
                        {res.existe ? "Documento auténtico" : "No encontrado en nuestros registros"}
                    </div>
                    {res.detalle && (
                        <div className="mt-1 opacity-85 leading-relaxed">{res.detalle}</div>
                    )}
                </div>
            )}
        </div>
    );
}

function VerificadoresPublicos() {
    const widgets = [
        {
            testid: "cotizacion",
            titulo: "Verificar cotización",
            descripcion: "¿Recibiste una propuesta COT-? Confirma que fue emitida por Trafalgar.",
            placeholder: "COT-2026-0001",
            endpoint: (v) => `/api/public/verificar-cotizacion/${v}`,
            parseResult: (d) => ({
                existe: !!d.existe,
                detalle: d.existe
                    ? `${d.folio || ""} · estado ${d.estado || "—"} · vigente hasta ${(d.vigente_hasta || "").slice(0, 10) || "—"}`
                    : "El folio no corresponde a una cotización emitida por Trafalgar.",
            }),
        },
        {
            testid: "estado-cuenta",
            titulo: "Verificar estado de cuenta",
            descripcion: "¿Tienes un folio EC- de un beneficiario? Confirma su autenticidad.",
            placeholder: "EC-TRF-2026-10-0001",
            endpoint: (v) => `/api/public/verificar-estado-cuenta/${v}`,
            parseResult: (d) => ({
                existe: !!d.existe,
                detalle: d.existe
                    ? `${d.folio} · periodo ${d.periodo?.anio || ""}/${String(d.periodo?.mes || "").padStart(2, "0")} · versión v${d.version || 1}`
                    : "El folio no corresponde a un estado de cuenta emitido por Trafalgar.",
            }),
        },
        {
            testid: "nom151",
            titulo: "Verificar sello temporal",
            descripcion: "¿Un documento tiene sello NOM-151? Pégalo aquí para validar la fecha oficial.",
            placeholder: "ID del sello (24 caracteres)",
            endpoint: (v) => `/api/public/nom151/verificar/${v}`,
            parseResult: (d) => ({
                existe: !!d.existe,
                detalle: d.existe
                    ? `Autoridad ${d.autoridad_tsa || "ADVANTIS_MOCK"} · fecha oficial ${(d.fecha_sello || "").slice(0, 10)}`
                    : "El sello no corresponde a un documento sellado por Trafalgar.",
            }),
        },
        {
            testid: "portafolio",
            titulo: "Verificar portafolio",
            descripcion: "Consulta el snapshot inmutable de un fideicomiso para una fecha dada.",
            placeholder: "trafalgar/2026-10-01",
            endpoint: (v) => `/api/public/verificar-portafolio/${v}`,
            parseResult: (d) => ({
                existe: !!(d && (d.fecha || d.snapshot_id)),
                detalle: (d && (d.fecha || d.snapshot_id))
                    ? `Snapshot ${d.snapshot_id || d.fecha} · tenant ${d.tenant_slug || "—"}`
                    : (d && d.detail) || "No se encontró snapshot para ese tenant y fecha.",
            }),
        },
    ];
    return (
        <section id="verificadores" className="py-20 lg:py-24 bg-[color:var(--brand-paper)] border-y border-[color:var(--brand-charcoal)]/5"
                 data-testid="verificadores-publicos">
            <div className={SECTION}>
                <div className="max-w-2xl mb-10">
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-3">
                        Verificadores públicos
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl text-[color:var(--brand-charcoal)] leading-tight">
                        Nada que creernos. Verifícalo tú.
                    </h2>
                    <p className="mt-4 text-sm md:text-base text-[color:var(--brand-charcoal)]/75 leading-relaxed">
                        Toda cotización, estado de cuenta y sello temporal que emitimos
                        es consultable por folio sin registro ni fricción. Pégalo aquí
                        y confirma su autenticidad en segundos.
                    </p>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                    {widgets.map((w) => (
                        <VerifWidget key={w.testid} {...w} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function FooterPublic() {
    return (
        <footer className="bg-[color:var(--brand-charcoal)] text-[rgba(251,249,245,0.8)] pt-16 pb-10"
            data-testid="landing-footer">
            <div className={SECTION}>
                <div className="grid md:grid-cols-5 gap-10 mb-10">
                    <div className="md:col-span-2">
                        <img
                            src="/brand/logo_horizontal_light.png"
                            alt="Trafalgar Servicios Fiduciarios"
                            className="h-14 object-contain mb-4"
                        />
                        <p className="text-sm leading-relaxed text-[rgba(251,249,245,0.7)] max-w-sm">
                            Dirección Fiduciaria de TRF Casa de Bolsa, S.A. de C.V. Fideicomisos
                            de garantía, administración, escrow, inversión y planeación
                            patrimonial con transparencia operativa.
                        </p>
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-[color:var(--brand-gold)] font-semibold mb-4">
                            Producto
                        </div>
                        <ul className="space-y-2 text-sm">
                            <li><a href="#promesas" className="hover:text-[color:var(--brand-gold)]">Promesa Trafalgar</a></li>
                            <li><a href="#audiencia" className="hover:text-[color:var(--brand-gold)]">Para quién</a></li>
                            <li><a href="#verificadores" className="hover:text-[color:var(--brand-gold)]" data-testid="footer-verificadores">Verificadores públicos</a></li>
                            <li><a href="/api/public/ficha-tecnica.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-[color:var(--brand-gold)]">Ficha técnica (PDF)</a></li>
                            <li><Link to="/login" className="hover:text-[color:var(--brand-gold)]">Acceder a la plataforma</Link></li>
                        </ul>
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-[color:var(--brand-gold)] font-semibold mb-4">
                            Confianza
                        </div>
                        <ul className="space-y-2 text-sm">
                            <li>Autorizada CNBV</li>
                            <li>Protocolo NOM-151</li>
                            <li>Bitácora inmutable</li>
                            <li><a href="/api/public/verificar-portafolio/trafalgar/latest" target="_blank" rel="noopener noreferrer" className="hover:text-[color:var(--brand-gold)]">Portafolio público</a></li>
                        </ul>
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-[color:var(--brand-gold)] font-semibold mb-4">
                            Contacto
                        </div>
                        <ul className="space-y-2 text-sm">
                            <li className="flex items-start gap-2">
                                <Mail className="h-3.5 w-3.5 mt-1 shrink-0" />
                                <span>contacto@trafalgar.mx</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Phone className="h-3.5 w-3.5 mt-1 shrink-0" />
                                <span>+52 55 5000 0000</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <MapPin className="h-3.5 w-3.5 mt-1 shrink-0" />
                                <span>Av. Santa Fe 481, Piso 8<br />Cuajimalpa, CDMX 05348</span>
                            </li>
                        </ul>
                    </div>
                </div>
                <div className="pt-8 border-t border-[rgba(251,249,245,0.15)] flex flex-col md:flex-row justify-between gap-4 text-xs text-[rgba(251,249,245,0.5)]">
                    <div>
                        © 2026 Trafalgar Servicios Fiduciarios · TRF Casa de Bolsa, S.A. de C.V.
                        Todos los derechos reservados.
                    </div>
                    <div className="flex gap-5">
                        <a href="#" className="hover:text-[color:var(--brand-gold)]">Aviso de privacidad</a>
                        <a href="#" className="hover:text-[color:var(--brand-gold)]">Términos</a>
                        <a href="#" className="hover:text-[color:var(--brand-gold)]">Cookies</a>
                    </div>
                </div>
            </div>
        </footer>
    );
}

/* ============================================================
   LANDING ROOT
   ============================================================ */
export default function Landing() {
    useEffect(() => {
        document.title = "Trafalgar Servicios Fiduciarios · Fiduciario con transparencia en tiempo real";
        const existing = document.querySelector("meta[name=description]");
        const content = "Casa de bolsa autorizada por la CNBV. Fideicomisos de garantía, administración y escrow con portal público para acreedores. Aceptación en 10 días hábiles.";
        if (existing) {
            existing.setAttribute("content", content);
        } else {
            const m = document.createElement("meta");
            m.setAttribute("name", "description");
            m.setAttribute("content", content);
            document.head.appendChild(m);
        }
    }, []);

    const [menuOpen, setMenuOpen] = useState(false);
    // Pre-fill del rol en el formulario según la audiencia que haga click
    const [preRol, setPreRol] = useState("");

    // Si entra con ?audiencia=xxx (link directo), aplicar pre-fill y scroll
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const aud = params.get("audiencia");
        if (aud && AUDIENCIA_TO_ROL[aud]) {
            setPreRol(AUDIENCIA_TO_ROL[aud]);
            setTimeout(() => {
                document.getElementById("contacto")
                    ?.scrollIntoView({ behavior: "smooth" });
            }, 300);
        }
    }, []);

    const scrollTo = (id) => {
        setMenuOpen(false);
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    };
    const scrollToForm = () => scrollTo("contacto");

    // Handler para cuando una card de audiencia dispara "Hablar con un asesor"
    const scrollToFormWithAudiencia = (audienciaKey) => {
        if (audienciaKey && AUDIENCIA_TO_ROL[audienciaKey]) {
            setPreRol(AUDIENCIA_TO_ROL[audienciaKey]);
            // Actualizar la URL sin recargar
            const url = new URL(window.location.href);
            url.searchParams.set("audiencia", audienciaKey);
            window.history.replaceState({}, "", url.toString());
        }
        scrollTo("contacto");
    };

    return (
        <div
            className="min-h-screen overflow-x-hidden bg-[color:var(--brand-paper)] text-[color:var(--brand-charcoal)]"
            data-testid="landing-page"
        >
            {/* Header sticky */}
            <header className="sticky top-0 z-50 backdrop-blur-md bg-[color:var(--brand-paper)]/90 border-b border-[color:var(--brand-charcoal)]/10">
                <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 py-3.5">
                    <a href="/" className="flex items-center gap-2.5" data-testid="landing-logo">
                        <img
                            src="/brand/logo_horizontal_dark.png"
                            alt="Trafalgar Servicios Fiduciarios"
                            className="h-11 object-contain"
                        />
                    </a>
                    <nav className="hidden md:flex items-center gap-8 text-sm">
                        <button onClick={() => scrollTo("promesas")} className="hover:text-[color:var(--brand-dark-gold)] transition" data-testid="nav-promesas">
                            Promesa
                        </button>
                        <button onClick={() => scrollTo("audiencia")} className="hover:text-[color:var(--brand-dark-gold)] transition" data-testid="nav-audiencia">
                            Para quién
                        </button>
                        <button onClick={() => scrollTo("contacto")} className="hover:text-[color:var(--brand-dark-gold)] transition" data-testid="nav-contacto">
                            Contacto
                        </button>
                    </nav>
                    <div className="flex items-center gap-3">
                        <Link to="/login">
                            <Button size="sm" variant="outline" data-testid="landing-header-acceder">
                                Acceder
                            </Button>
                        </Link>
                        <Button size="sm" onClick={scrollToForm} className="hidden sm:inline-flex" data-testid="landing-header-agendar">
                            Agendar
                        </Button>
                        <button
                            className="md:hidden"
                            onClick={() => setMenuOpen(!menuOpen)}
                            aria-label="Menú"
                            data-testid="landing-menu-btn"
                        >
                            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </button>
                    </div>
                </div>
                {menuOpen && (
                    <div className="md:hidden border-t border-[color:var(--brand-charcoal)]/10 bg-[color:var(--brand-paper)]">
                        <div className="flex flex-col p-6 gap-4 text-sm">
                            <button onClick={() => scrollTo("promesas")}>Promesa</button>
                            <button onClick={() => scrollTo("audiencia")}>Para quién</button>
                            <button onClick={() => scrollTo("contacto")}>Contacto</button>
                        </div>
                    </div>
                )}
            </header>

            {/* Secciones */}
            <HeroSection onCta={scrollToForm} />
            <PainsSection />
            <PromisesSection />
            <AudienceSection onCta={scrollToFormWithAudiencia} />
            <StatsBar />
            <ComparisonSection />
            <CaseStudySection />
            <TrustBadges />
            <VerificadoresPublicos />

            {/* Form */}
            <section
                id="contacto"
                className="py-20 lg:py-24 bg-[color:var(--brand-charcoal)] text-[color:var(--brand-paper)]"
                data-testid="landing-contacto"
            >
                <div className="max-w-5xl mx-auto px-6 lg:px-10">
                    <div className="grid lg:grid-cols-5 gap-10 lg:gap-14">
                        <div className="lg:col-span-2 min-w-0">
                            <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-gold)] font-semibold mb-3">
                                Empieza aquí
                            </div>
                            <h2 className="font-display text-3xl md:text-4xl mb-5 leading-tight">
                                ¿Hablamos 20 minutos esta semana?
                            </h2>
                            <p className="text-sm text-[rgba(251,249,245,0.75)] leading-relaxed mb-7">
                                Un asesor del equipo escucha tu operación, te dice si cabe en
                                nuestro catálogo y te envía cotización indicativa dentro de 24
                                horas. Sin compromiso.
                            </p>
                            <ul className="space-y-3 text-sm text-[rgba(251,249,245,0.85)]">
                                <li className="flex items-start gap-3">
                                    <CheckCircle2 className="h-4 w-4 text-[color:var(--brand-gold)] shrink-0 mt-0.5" />
                                    <span>Respuesta en menos de 2 días hábiles</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <CheckCircle2 className="h-4 w-4 text-[color:var(--brand-gold)] shrink-0 mt-0.5" />
                                    <span>Cotización formal sin letras chicas</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <CheckCircle2 className="h-4 w-4 text-[color:var(--brand-gold)] shrink-0 mt-0.5" />
                                    <span>Confidencialidad desde el primer correo</span>
                                </li>
                            </ul>
                        </div>
                        <div className="lg:col-span-3 min-w-0 bg-[color:var(--brand-paper)] text-[color:var(--brand-charcoal)] rounded-lg p-7 md:p-10">
                            <ContactForm initialRol={preRol} />
                        </div>
                    </div>
                </div>
            </section>

            <FAQSection />
            <FooterPublic />
        </div>
    );
}
